/**
 * THE PUBLIC TUTORIALS — source of truth for the /faq page's guides, the HowTo JSON-LD and
 * /llms.txt.
 *
 * Same AEO rules as content/faq.ts: each tutorial stands alone, the steps are literal actions in
 * the order the product actually enforces them, and numbers are concrete. HowTo structured data
 * wants short imperative step names with the detail in the step text, which is why they are split.
 *
 * ⚠️ THE ORDER OF TUTORIALS 2 AND 3 IS LOAD-BEARING, not editorial. A subscription is bought for
 * one specific exchange account, so the account must exist before it can be paid for. Documenting
 * "subscribe, then connect" would send every new user into a dead end — the same dead end that was
 * built and removed from the product twice.
 */

export type Tutorial = {
  /** URL anchor — stable, since an answer engine may cite `/faq#…`. */
  id: string;
  title: string;
  /** One-sentence answer to "what does this get me", first — an engine that truncates keeps it. */
  intro: string;
  minutes: number;
  prerequisites: string[];
  steps: { name: string; text: string }[];
  /** Shown as a callout and appended to the JSON-LD description. Use for the trap, not a summary. */
  note?: string;
};

export const TUTORIALS: Tutorial[] = [
  {
    id: "create-your-twoke-account",
    title: "Create your twoke account",
    intro:
      "Sign up with an email address and a password, and accept the two consents twoke requires before any trading feature unlocks.",
    minutes: 3,
    prerequisites: ["An email address you can receive mail at."],
    steps: [
      {
        name: "Open the sign-up page",
        text: "Go to the twoke home page and choose Get started, or go directly to /signup. The trading risk disclosure appears first — read it. It states plainly that leveraged futures trading can lose some or all of your crypto, that copy trading is not financial advice, and that subscriptions are non-refundable.",
      },
      {
        name: "Accept the risk disclosure",
        text: "Agreeing records the disclosure against your account with its version number. If the disclosure text is later updated, you are asked to accept the new version before you can connect a key or subscribe again. This is enforced by the server, not just the page, so it cannot be skipped by going straight to the API.",
      },
      {
        name: "Enter your email and choose a password",
        text: "Passwords must be at least 12 characters. For your security a session lasts one hour from sign-in. Tick remember-me to keep it if you close and reopen the browser within that hour, or leave it unticked for a session that ends when you close the browser. If you would rather not use a password at sign-in, you can request a 6-digit email code instead from the login page at any time.",
      },
      {
        name: "Accept the Terms of Service",
        text: "The Terms carry the no-refund policy, so acceptance is recorded with a version against your account and is required before you can subscribe to a master or connect an exchange key. You will be re-prompted if the Terms version changes.",
      },
    ],
    note: "Creating an account does not cost anything and does not connect to any exchange. Nothing can trade until you connect an exchange key and buy a subscription, in that order.",
  },

  {
    id: "connect-your-exchange-api-key",
    title: "Connect your Bybit, Binance or MEXC API key",
    intro:
      "Create a trade-only API key on your exchange and connect it to twoke, so a master's trades can be placed on your own account. Do this before subscribing — a subscription is bought for a specific exchange account.",
    minutes: 10,
    prerequisites: [
      "A twoke account with the risk disclosure and Terms accepted.",
      "A Bybit, Binance or MEXC account. On Bybit or Binance a demo or testnet account is enough to start; MEXC has no demo.",
      "Access to the email address on your twoke account, for the step-up code.",
    ],
    steps: [
      {
        name: "Create an API key on your exchange",
        text: "On Bybit, open API Management and create a key with Contract trading and read permissions. On Binance, open API Management and enable Reading and Futures. On both, leave withdrawal permission OFF — twoke never needs it, and a key that cannot withdraw cannot move your funds even if it leaks. Copy the API key and secret; the secret is shown only once.",
      },
      {
        name: "Choose demo or live",
        text: "Decide whether this key points at the exchange's demo/testnet environment or at real funds. Demo is the default and is the recommended way to start — a demo follower receives exactly the same trades, sizing and stop-loss handling as a live one, against a demo balance. Create the key in the matching environment: a demo-scoped key cannot authenticate against the live venue.",
      },
      {
        name: "Request your step-up code",
        text: "In the twoke dashboard, open the Accounts section and start connecting an account. Request a step-up code, which is emailed to you as 6 digits. It expires after 5 minutes, works once, and is stored only as a hash. Connecting a key requires it even though you are already signed in — your password is the factor you know, this code is the factor you have.",
      },
      {
        name: "Enter the key, secret and a label",
        text: "Paste the API key and secret, pick the exchange, confirm the demo or live setting, and give the account a label you will recognise later — the label is how you tell your accounts apart in the dashboard. The secret is encrypted with AES-256-GCM the moment it arrives and is never stored or logged in readable form.",
      },
      {
        name: "Confirm the account appears",
        text: "The account now appears in your dashboard with a truncated API key. It will also say that it has no subscription and is not copying anything. That is expected and correct: a connected key with no subscription is inert — the copy allocator only selects accounts with an active subscription, so nothing will be placed until you subscribe.",
      },
    ],
    note: "You can hold one connected account without a subscription. A second idle account is refused until the first one is subscribed, because every connected account opens a private exchange WebSocket and exchanges cap how many connections come from one address.",
  },

  {
    id: "subscribe-to-a-master",
    title: "Subscribe to a master and pay from your own wallet",
    intro:
      "Buy a subscription for one master on one of your connected exchange accounts, paying from your own wallet. The steps below use Polygon (USDC, USDT or POL); Ethereum (USDC, USDT or ETH, from the same wallets) and NEAR (USDC, USDT or NEAR, from Meteor Wallet, HERE, HOT or Nightly) are offered at checkout too.",
    minutes: 10,
    prerequisites: [
      "A connected exchange account (see the previous tutorial — the subscription is bought for a specific account).",
      "A wallet such as MetaMask holding native USDC on Polygon PoS.",
      "A small amount of POL for gas. Polygon gas is paid in POL, not MATIC.",
    ],
    steps: [
      {
        name: "Choose a master",
        text: "Pick the master you want to follow. One exchange account can follow exactly one master, so if the account you pick already follows another, subscribe a different account instead.",
      },
      {
        name: "Choose a plan",
        text: "Quarterly (3 months), semi-annual (6 months) or annual (12 months). There is no monthly plan. The price shown at checkout is the live price, read at that moment rather than from a published table, so it is always current.",
      },
      {
        name: "Select the exchange account",
        text: "Pick which of your connected accounts this subscription entitles. This matters: the account identifier goes into the on-chain payment, and the subscription is bound to that exact account afterwards. Paying for one account does not entitle the others.",
      },
      {
        name: "Connect your wallet and choose how to pay",
        text: "Connect MetaMask or another wallet on Polygon PoS, chain ID 137. Pick USDC, USDT or POL. For a stablecoin you sign a one-time permit for exactly the plan price (a signature, no separate approval transaction, nothing stays approved); for POL the contract charges the plan price at the live Chainlink rate and returns any excess. twoke accepts native USDC issued by Circle; the older bridged token labelled USDC.e is a different contract and is not accepted, so check which one your wallet holds if the approval is rejected.",
      },
      {
        name: "Confirm the payment",
        text: "Confirm the transaction and wait for it to be included on-chain. Your subscription activates when the payment service observes the confirmed transaction, not when the browser returns — so a slow confirmation is not a failed payment. Once active, the account starts receiving that master's trades.",
      },
    ],
    note: "Subscriptions are non-refundable — not partial, not pro-rated, not for downtime. Nothing renews automatically, because payment comes from your own wallet and no payment method is stored. If a payment lands for an account that cannot receive it, it is held as a pending subscription for you to attach rather than rejected.",
  },

  {
    id: "set-your-risk-settings",
    title: "Set your risk limits before the first copied trade",
    intro:
      "Configure how much of your own account each copied trade may risk, and the caps that can veto a trade entirely. Every setting here can only shrink or refuse a trade — none of them can make one larger.",
    minutes: 5,
    prerequisites: [
      "A twoke account with at least one connected exchange account.",
    ],
    steps: [
      {
        name: "Open the risk panel",
        text: "In your dashboard, open the risk settings panel. Settings apply per master, with a default that covers any master you have not set individually.",
      },
      {
        name: "Set risk per trade",
        text: "This is the share of the assets in your account you lose if a trade reaches its stop-loss. The default is 0.35% and the maximum accepted is 3%. Position size is derived from it: the assets in your account are read from the exchange immediately before each trade, multiplied by this percentage, then divided by the distance to the stop — so the same setting produces a smaller position when the stop is further away.",
      },
      {
        name: "Set your concurrent position and daily loss caps",
        text: "Maximum concurrent positions limits how many copied positions can be open at once; the default is 3. Maximum daily loss stops new copies for the rest of the day once the day's result (closed trades, fees and funding, plus open trades' floating profit or loss) reaches that share of the assets in your account when it first subscribed — the day turns with the exchanges' day at 00:00 UTC (8 PM New York in summer, 7 PM in winter) for crypto, gold and oil alike; the default is 3%, and the figure is read from your exchange's own booked profit-and-loss rather than estimated.",
      },
      {
        name: "Set your copy deviation limit",
        text: "This is how far price may move between the master's entry and your copy before the copy is skipped. The default is 0.5%. A copy outside it is skipped rather than resized, because entering later at a worse price with the same stop-loss means risking more than you configured.",
      },
      {
        name: "Know where the kill switch is",
        text: "The kill switch stops new copied trades for a master immediately and needs no confirmation code — a control that stops trading must never wait on an email. It does not close positions already open, and it leaves their stop-loss and take-profit live at the exchange. Find it before you need it.",
      },
    ],
  },

  {
    id: "disconnect-or-rotate-a-key",
    title: "Rotate, move or remove an exchange key",
    intro:
      "Replace a key on an existing account without losing your subscription, move an account between demo and live, or remove a connected account entirely.",
    minutes: 5,
    prerequisites: [
      "Access to the email address on your twoke account, for the step-up code.",
      "For a demo/live move or a removal: the account must be flat, with no open positions.",
    ],
    steps: [
      {
        name: "Decide whether you need to rotate or remove",
        text: "Rotate if the account should keep existing — regenerating a key on the exchange, or moving between demo and live. Rotation replaces the key and secret on the same account, so the subscription bound to it, the risk settings and any open positions all stay attached. Remove only if you want the connected account gone.",
      },
      {
        name: "Rotate the key",
        text: "Request a step-up code, then submit the new API key and secret for that account. The exchange cannot be changed by rotation — a Bybit key and a Binance key are different accounts — but the demo/live setting can, in which case the key must change with it, since a demo-scoped key cannot authenticate against the live venue.",
      },
      {
        name: "Flatten the account first if you are moving demo to live",
        text: "Moving an account between demo and live requires no open positions. This is refused rather than warned about, because each environment is watched by a separate process: moving an account with a live position would leave that position running without the watchdog that keeps its stop-loss attached.",
      },
      {
        name: "Remove the account",
        text: "Removal needs three things: a verified step-up code, no active subscription on that account, and no open positions. The subscription condition exists so crypto already paid cannot be stranded by a deletion. If the stored key no longer authenticates at all, removal is allowed anyway — at that point twoke can neither see nor manage the account, so refusing would only trap you.",
      },
    ],
    note: "Removing a key stops copying for that account immediately, but does not close positions already open. Their stop-loss and take-profit remain live at the exchange, and you can manage them there.",
  },
];

/** Flatten a tutorial for /llms.txt and the JSON-LD description. Derived, never restated. */
export const tutorialText = (t: Tutorial): string =>
  [
    t.intro,
    t.prerequisites.length
      ? `Before you start: ${t.prerequisites.join(" ")}`
      : "",
    ...t.steps.map((s, i) => `${i + 1}. ${s.name} — ${s.text}`),
    t.note ?? "",
  ]
    .filter(Boolean)
    .join("\n");
